import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';

import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomChip from '@ui-components/Chip/CustomChip';

import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { rowsPerPage } from '../constants';

import { useNavigate, useLocation } from 'react-router-dom';
import CustomPagination from '@ui-components/Table/CustomPagination';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import useDebouncing from '../components/useDebouncing';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import TransferList from '../components/TransferList';
import './intake.css';
import {
  useGetIntakeFormList,
  useGetFacilityMappingList,
  useUpdateDefaultIntakeForm,
  useDeleteIntakeForm,
} from '../../API/hooks/IntakeFormHook';
import type { FacilityMappingList } from '../../API/services/IntakeFormService';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar.js';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomDialogActions from '@ui-components/Dialog/CustomDialogActions';
import EditOnlyIcon from '@assets/images/editOnly.svg';
import DeleteIcon from '@assets/images/delete_table.svg';
import {
  INTAKE_ROUTE_PATH,
  NEW_INTAKE_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomToolTip from '@qler-view/shared_lib/src/shared/lib/ui-components/ToolTip/CustomToolTip';
import CustomPopover from '@qler-view/shared_lib/src/shared/lib/ui-components/Popover/CustomPopover';
import PartnerFacilitySelector from '../components/PartnerFacilitySelector';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';
import { CustomBadge, CustomDivider } from '@qler-view/shared_lib/src';
import type { SelectedState } from '../../API/services/PartnerFacilityFilterService';
import { hasRole } from '../login/storageUtility';
import { formatFieldValue } from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import { useFilters } from '../../routers/FilterContext';
const IntakeFormList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');
  const userId = localStorage.getItem('userId') ?? '0';
  const [anchorIntakeEl, setAnchorIntakeEl] = useState<HTMLElement | null>(
    null
  );
  const [openIntakeFormFacilityMapping, setOpenIntakeFormFacilityMapping] =
    useState(false);

  const { filterContext, setFilterContext, getFilterCounts } = useFilters();

  const [openSetAsDefault, setOpenSetAsDefault] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const handleOpenSetAsDefault = (state: boolean) => {
    setOpenSetAsDefault(state);
  };

  const [selectedRowIdForDelete, setSelectedRowIdForDelete] = useState<
    number | null
  >(null);

  const [selectedFacilities, setSelectedFacilities] = useState<SelectedState>(
    filterContext.facilities
  );
  const [filteredFacilities, setFilteredFacilities] = useState<SelectedState>(
    filterContext.facilities
  );

  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );

  const [selectedData, setSelectedData] = useState<FacilityMappingList | null>(
    null
  );

  const [searchTermIntakeFormList, setSearchTermIntakeFormList] =
    useState<string>(filterContext.searchedValue);
  const debouncedSearchTerm = useDebouncing(searchTermIntakeFormList, 500);

  const inputRef = useRef<HTMLInputElement>(null);

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  const handleSelectedFacilities = (data: SelectedState) => {
    setFilteredFacilities(data);
  };
  const handleClearFilter = () => {
    setSelectedFacilities({});
    setFilteredFacilities({});
    setFilterContext({
      ...filterContext,
      facilities: {},
    });
  };

  const handleOpenDeleteConfirmation = (state: boolean) => {
    setOpenDeleteConfirmation(state);
  };

  const handleOpenIntakeFormFacilityMapping = (state: boolean) => {
    setOpenIntakeFormFacilityMapping(state);
  };

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Intake form saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Intake form updated successfully!');
      setOpenToast(true);
    }
    navigate(INTAKE_ROUTE_PATH, { replace: true });
  }, [toast, navigate]);
  const onSuccessTost = (type: string) => {
    if (type === 'delete') {
      setMessage('Intake form deleted successfully!');
    } else if (type === 'default') {
      setMessage('Succesfully set as default intake form');
    }
    setOpenToast(true);
    setTimeout(() => {
      setOpenToast(false);
    }, 3000);
  };

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  const { fetchFacilityMappingList } = useGetFacilityMappingList(userId);

  const { mutate: updateDefaultIntakeForm } = useUpdateDefaultIntakeForm(() =>
    onSuccessTost('default')
  );

  const { mutate: DeleteIntakeForm } = useDeleteIntakeForm(() =>
    onSuccessTost('delete')
  );

  const selectedFacilityIds = React.useMemo(
    () => Object.values(selectedFacilities).flat(),
    [selectedFacilities]
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedFacilityIds]);

  useEffect(() => {
    setFilteredFacilities(filterContext.facilities);
    setSearchTermIntakeFormList(filterContext.searchedValue);
  }, [filterContext]);

  const { IntakeFormListResponse, isLoading } = useGetIntakeFormList({
    userId: userId || '',
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    facilityId: selectedFacilityIds,
  });
  const { PartnerFacilityFilterData, refetch: refetchFilters } =
    useGetPartnerFacilityFilterData();

  const handleViewFacilityMapping = async (intakeFormId: number) => {
    try {
      const data = await fetchFacilityMappingList(intakeFormId);
      setSelectedData(data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSetDefaultIntakeForm = async (defaultIntakeForm: boolean) => {
    if (selectedRowId !== null) {
      const intakeFormId = selectedRowId;
      try {
        updateDefaultIntakeForm({
          userId: userId,
          intakeFormId,
          defaultIntakeForm,
        });
      } catch (error) {
        console.error('Error Updating default intake form:', error);
      }
    }
  };

  const IntakeFormListCount = IntakeFormListResponse?.totalRecords ?? 0;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus the input field
    }
  }, [searchTermIntakeFormList]);

  const handleIntakeFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorIntakeEl(event.currentTarget);
    refetchFilters();
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handleIntakeFormListSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;

    // Remove leading spaces
    value = value.replace(/^\s+/, '');

    // Replace multiple spaces with a single space
    value = value.replaceAll(/\s{2,}/g, ' ');

    setSearchTermIntakeFormList(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleEditIntakeForm = (intakeFormId: number) => {
    navigate(NEW_INTAKE_ROUTE_PATH + `/${intakeFormId}`, { replace: true });
  };

  const handleDeleteIntakeForm = async () => {
    if (selectedRowIdForDelete !== null) {
      const intakeFormId = selectedRowIdForDelete;
      try {
        DeleteIntakeForm({ userId: userId, formId: intakeFormId });
      } catch (error) {
        console.error('Error Deleting Intake Form:', error);
      }
    }
  };

  const handleApplyFilters = () => {
    setSelectedFacilities(filteredFacilities);
    setAnchorIntakeEl(null);
    setFilterContext({ ...filterContext, facilities: filteredFacilities });
  };
  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="intake-form-tost"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title-intake-form-list"
              variant="h4"
              className="text-align-left"
            >
              Intake Forms
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-intake-form-list"
                  name="search-intake-form-list"
                  placeholder="Search Intake Forms"
                  size="small"
                  type="text"
                  value={searchTermIntakeFormList}
                  onChange={handleIntakeFormListSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleIntakeFilterClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-Intake-Form"
                    color="primary"
                    size="large"
                    onClick={() => {
                      navigate(NEW_INTAKE_ROUTE_PATH, { replace: true });
                    }}
                  >
                    Add Intake From
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CustomTypography
              id="loading-text"
              variant="h5"
              className="text-align-center pt-32"
              color="textDisabled"
            >
              Loading...
            </CustomTypography>
          </Box>
        ) : (
          <CustomTable id="table-intake-form-list">
            <CustomTableHead id="table-head-intake-form-list">
              <CustomTableRow id="clinicians-intake-form-header">
                <CustomTableCell
                  id="table-cell-intake-form-id"
                  onClick={() => handleSort('intakeFormBusinessId')}
                >
                  FORM ID &nbsp;
                  {sortField === 'intakeFormBusinessId' &&
                    getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-form-name"
                  onClick={() => handleSort('intakeFormName')}
                >
                  FORM NAME &nbsp;
                  {sortField === 'intakeFormName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-pid"
                  onClick={() => handleSort('pid')}
                >
                  PID &nbsp;
                  {sortField === 'pid' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell id="table-cell-assigned-facilities">
                  ASSIGNED FACILITIES &nbsp;
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-intake-form-created-date"
                  onClick={() => handleSort('createdOn')}
                >
                  CREATED ON &nbsp;
                  {sortField === 'createdOn' && getSortIcon(sortType)}
                </CustomTableCell>

                <CustomTableCell id="table-cell-default-intake-form">
                  DEFAULT INTAKE FORM &nbsp;
                </CustomTableCell>
                {hasRole(['ops-admin']) && (
                  <CustomTableCell id="table-cell-actions">
                    ACTIONS
                  </CustomTableCell>
                )}
              </CustomTableRow>
            </CustomTableHead>
            {IntakeFormListResponse &&
            IntakeFormListResponse?.intakeFormList.length > 0 ? (
              <CustomTableBody id="table-body-intake-form">
                {IntakeFormListResponse?.intakeFormList.map((intakeForm) => (
                  <CustomTableRow
                    key={intakeForm.id}
                    id={`table-row-intake-form-id-${intakeForm.id}`}
                  >
                    <CustomTableCell
                      id={`table-cell-intake-form-id-${intakeForm.id}`}
                    >
                      {intakeForm.intakeFormBusinessId}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-intake-form-name-${intakeForm.id}`}
                    >
                      {intakeForm.intakeFormName}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-intake-form-pid-${intakeForm.id}`}
                    >
                      {intakeForm.pid}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-intake-form-assigned-facilities-${intakeForm.id}`}
                    >
                      <Box display="flex" alignItems="center">
                        <CustomLink
                          id="assigned-facilities-link"
                          onClick={() => {
                            handleOpenIntakeFormFacilityMapping(true);
                            handleViewFacilityMapping(intakeForm.id);
                          }}
                        >
                          View Facilities
                        </CustomLink>
                        &nbsp;
                        <CustomTypography
                          id="assigned-facilities-count"
                          variant="body3"
                          className="facility-count"
                        >
                          {`(${intakeForm.assignedFacility})`}
                        </CustomTypography>
                      </Box>
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-intake-form-created-date-${intakeForm.id}`}
                    >
                      {formatFieldValue(intakeForm.createdOn)}
                    </CustomTableCell>

                    <CustomTableCell
                      id={`table-cell-intake-form-default-${intakeForm.id}`}
                    >
                      {intakeForm.default === true ? (
                        'Default'
                      ) : (
                        <CustomLink
                          id="set-as-default-link"
                          onClick={() => {
                            setSelectedRowId(intakeForm.id);
                            handleOpenSetAsDefault(true);
                          }}
                          className={
                            hasRole(['ops-admin']) ? '' : 'link-disabled'
                          }
                          disabled={!hasRole(['ops-admin'])}
                        >
                          Set as Default
                        </CustomLink>
                      )}
                    </CustomTableCell>
                    {hasRole(['ops-admin']) && (
                      <CustomTableCell
                        id={`table-cell-intake-form-actions-${intakeForm.id}`}
                        className="width-10"
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <CustomIconButton
                            id="edit-icon"
                            size="small"
                            icon={<EditOnlyIcon />}
                            onClick={() => {
                              handleEditIntakeForm(intakeForm.id);
                            }}
                          />
                          {intakeForm.default === true ? (
                            <CustomToolTip
                              id="tooltip-delete-icon"
                              title="To delete the default intake form, please set another intake form as default."
                              className="cursor-pointer"
                            >
                              <CustomIconButton
                                id="delete-icon"
                                size="small"
                                icon={<DeleteIcon />}
                                disabled
                              />
                            </CustomToolTip>
                          ) : (
                            <CustomIconButton
                              id="delete-icon"
                              size="small"
                              icon={<DeleteIcon />}
                              onClick={() => {
                                setSelectedRowIdForDelete(intakeForm.id);
                                handleOpenDeleteConfirmation(true);
                              }}
                            />
                          )}
                        </Box>
                      </CustomTableCell>
                    )}
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            ) : (
              <CustomTableBody id="table-body-intake-form">
                <CustomTableRow id="table-row-intake-form">
                  <CustomTableCell id="table-cell-intake-form-id" colspan={8}>
                    <Box className="flex flex-justify-center align-items-center">
                      <CustomTypography
                        id="no-intake-form-found"
                        variant="body2"
                        color="textDisabled"
                      >
                        No Intake Forms Found
                      </CustomTypography>
                    </Box>
                  </CustomTableCell>
                </CustomTableRow>
              </CustomTableBody>
            )}
          </CustomTable>
        )}
        {IntakeFormListResponse && IntakeFormListCount > 0 && (
          <Box className="mt-32">
            <Grid2 container className="align-items-center">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <Box className="flex justify-content-start">
                  <CustomTypography
                    id="showing-row-count-text"
                    variant="body2"
                    color="textDisabled"
                  >
                    Showing {getStartItem(page, rowsPerPage)} -{' '}
                    {getEndItem(page, rowsPerPage, IntakeFormListCount)} of{' '}
                    {IntakeFormListCount} Intake Forms
                  </CustomTypography>
                </Box>
              </Grid2>
              {IntakeFormListCount > rowsPerPage && (
                <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                  <Box display="flex" justifyContent="end">
                    <CustomPagination
                      id="intake-form-list-pagination"
                      count={Math.ceil(IntakeFormListCount / rowsPerPage)}
                      page={page + 1}
                      onPageChange={handlePageChange}
                      color="standard"
                    />
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Box>
      <Box>
        <TransferList
          assignedFacilities={selectedData?.assignedFacilities || []}
          allAvailableFacilities={selectedData?.allAvailableFacilities || []}
          intakeFormName={selectedData?.intakeFormName || ''}
          intakeFormId={selectedData?.intakeFormId || 0}
          openDialog={openIntakeFormFacilityMapping}
          onOpenDialog={handleOpenIntakeFormFacilityMapping}
        />
      </Box>

      {/* popup */}
      <Box>
        <CustomDialog
          id="intake-form-dialog"
          open={openSetAsDefault}
          onClose={() => {
            handleOpenSetAsDefault(true);
          }}
          disableEscapeKeyDown={true}
        >
          <CustomDialogContent className="intake-dialog-width">
            <Box className="pb-8">
              <CustomTypography id="intake-form-title" variant="h4">
                Change Default Intake Form
              </CustomTypography>
            </Box>
            <Box>
              <CustomTypography
                id="intake-form-text"
                variant="body3"
                color="textDisabled"
              >
                Changing the default intake form will apply system-wide and may
                impact existing configurations. Please confirm before
                proceeding.
              </CustomTypography>
            </Box>
          </CustomDialogContent>

          <CustomDialogActions>
            <Grid2 container>
              <Grid2 className="mr-8">
                <CustomButton
                  id="btnYes"
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    handleOpenSetAsDefault(false);
                  }}
                >
                  Cancel
                </CustomButton>
              </Grid2>
              <Grid2>
                <CustomButton
                  id="btnConfirm-complete"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleSetDefaultIntakeForm(true);
                    handleOpenSetAsDefault(false);
                  }}
                >
                  Confirm
                </CustomButton>
              </Grid2>
            </Grid2>
          </CustomDialogActions>
        </CustomDialog>
      </Box>
      {/* Delete Popup */}
      <Box>
        <CustomDialog
          id="intake-form-delete-dialog"
          open={openDeleteConfirmation}
          onClose={() => {
            handleOpenDeleteConfirmation(true);
          }}
          disableEscapeKeyDown={true}
        >
          <CustomDialogContent className="intake-dialog-width">
            <Box className="pb-8">
              <CustomTypography id="intake-form-title" variant="h4">
                Confirm Action
              </CustomTypography>
            </Box>
            <Box>
              <CustomTypography
                id="intake-form-text"
                variant="body3"
                color="textDisabled"
              >
                Are you sure you want to delete this intake form? This action is
                irreversible.
              </CustomTypography>
            </Box>
          </CustomDialogContent>

          <CustomDialogActions>
            <Grid2 container>
              <Grid2 className="mr-8">
                <CustomButton
                  id="btnYes"
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    handleOpenDeleteConfirmation(false);
                  }}
                >
                  Cancel
                </CustomButton>
              </Grid2>
              <Grid2>
                <CustomButton
                  id="btnConfirm-complete"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleDeleteIntakeForm();
                    handleOpenDeleteConfirmation(false);
                  }}
                >
                  Confirm
                </CustomButton>
              </Grid2>
            </Grid2>
          </CustomDialogActions>
        </CustomDialog>
      </Box>
      {/* Intake filter */}
      <Box>
        <CustomPopover
          open={Boolean(anchorIntakeEl)}
          anchorEl={anchorIntakeEl}
          onClose={() => setAnchorIntakeEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24">
            <Box className="flex-between-center">
              <Box>
                <CustomTypography
                  id="intake-form-facility-mapping-title"
                  variant="h5"
                  color="textPrimary"
                >
                  Facilities
                </CustomTypography>
              </Box>
            </Box>
            <Box className="pt-16 pb-16">
              <CustomDivider />
            </Box>

            <Box>
              <PartnerFacilitySelector
                data={PartnerFacilityFilterData}
                value={filteredFacilities}
                onChange={handleSelectedFacilities}
                showPartnerCheckbox={false}
                singleFacilitySelection
              />
            </Box>
            <Box className="pt-16 display-flex justify-end">
              <Box className="pr-12">
                <CustomButton
                  id="cancel-facility-user-button"
                  variant="outlined"
                  onClick={() => {
                    handleClearFilter();
                    setAnchorIntakeEl(null);
                  }}
                >
                  Clear
                </CustomButton>
              </Box>
              <CustomButton
                id="intake-form-apply-filter"
                color="primary"
                variant="contained"
                onClick={() => {
                  handleApplyFilters();
                }}
              >
                Apply Filter
              </CustomButton>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default IntakeFormList;
